import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import "./clientEvent.css";
import { useAuth } from "../../context/AuthContext";
import useTokenFromUrl from "../../hooks/useTokenFromUrl";
import { bookEvent, validateBooking, createEventPaymentOrder, verifyEventPayment, fetchEventById } from "./clientEventService";
import axios from "axios";

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const BookingPage = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { user } = useAuth();
  const { loading: tokenLoading } = useTokenFromUrl();

  const [eventData, setEventData] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [countryCodes, setCountryCodes] = useState([]);
  const [mobileError, setMobileError] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "info", message: "" });

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
      .then(res => setCountryCodes(res.data?.data || []))
      .catch(() => {});
  }, []);

  const mapEvent = (e) => ({
    id: e.id,
    title: e.title || "Event",
    description: e.description || "",
    eventStartTime: e.eventStartTime,
    eventDuration: e.eventDuration || 60,
    speakerName: e.speakerName || "KK",
    speakerBio: e.speakerBio || "Public Speaking Coach | Speaker & Trainer",
    speakerImageUrl: e.speakerAvatarUrl || e.speakerImageUrl || null,
    originalPrice: e.originalPrice || 0,
    price: e.price || 0,
    category: e.category || "Workshop",
    cancelled: e.cancelled || false,
    topBannerText: e.topBannerText?.trim() || null,
    totalAttendees: e.totalAttendees ?? null,
    bonusValue: e.bonusValue ?? null,
    bonusDeadline: e.bonusDeadline ?? null,
    seatsLeft: e.seatsLeft ?? null,
    highlights: Array.isArray(e.highlights) ? e.highlights : [],
  });

  useEffect(() => {
    if (tokenLoading || !eventId) { setEventLoading(false); return; }
    fetchEventById(eventId).then(res => {
      if (res.status === 200 && res.data) {
        setEventData(mapEvent(res.data));
      }
      setEventLoading(false);
    });
  }, [tokenLoading, eventId]);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setMobile(user.mobile || "");
    }
  }, [user]);

  if (tokenLoading || eventLoading) {
    return (
      <>
        <Navbar />
        <div className="bk-loading-wrap">
          <div className="bk-spinner"></div>
          <p>{tokenLoading ? "Signing you in..." : "Loading event..."}</p>
        </div>
      </>
    );
  }

  if (!eventData) {
    return (
      <>
        <Navbar />
        <div className="bk-loading-wrap">
          <h3>Invalid booking request</h3>
          <p>Event information is missing.</p>
          <button className="bk-back-btn" onClick={() => navigate("/")}>← Back to Home</button>
        </div>
      </>
    );
  }

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 4000);
  };

  const formatDate = (value) => {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return value; }
  };

  const formatTime = (value) => {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    } catch { return value; }
  };

  const formatAttendees = (n) => {
    if (n >= 100000) return (n / 100000).toFixed(1).replace(".0", "") + " Lakh+";
    if (n >= 1000) return (n / 1000).toFixed(0) + "K+";
    return n + "+";
  };

  const discountPct = eventData.originalPrice > 0
    ? Math.round(100 - (eventData.price / eventData.originalPrice) * 100)
    : 0;

  const validateForm = () => {
    if (!name.trim()) { showAlert("warning", "Please enter your name"); return false; }
    if (!email.trim()) { showAlert("warning", "Please enter your email"); return false; }
    if (!mobile.trim()) { showAlert("warning", "Please enter your mobile number"); return false; }
    const fullNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    if (!/^\+[1-9]\d{1,14}$/.test(fullNumber)) { showAlert("warning", "Please enter a valid mobile number"); return false; }
    if (!consent) { showAlert("warning", "Please provide consent"); return false; }
    return true;
  };

  const validateBookingData = async () => {
    const fullNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    const validationResponse = await validateBooking(eventData.id, email.trim(), fullNumber.trim());
    if (validationResponse.status !== 200) { showAlert("error", validationResponse.message || "Validation failed"); return false; }
    const v = validationResponse.data?.data || validationResponse.data;
    if (v.emailExists) { showAlert("warning", "This email is already registered for this event"); return false; }
    if (v.mobileExists) { showAlert("warning", "This mobile number is already registered for this event"); return false; }
    if (!v.eventAvailable) { showAlert("error", "Event is no longer available or fully booked"); return false; }
    return true;
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handleBooking = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const isValid = await validateBookingData();
      if (!isValid) return;
      if (eventData.price <= 0) {
        await bookFreeEvent();
      } else {
        await initPaidCheckout();
      }
    } catch (error) {
      showAlert("error", error?.response?.data?.message || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  const bookFreeEvent = async () => {
    const fullMobileNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    const payload = {
      name: name.trim(), email: email.trim(), mobile: fullMobileNumber.trim(),
      countryCode: dialCode, timeZone: userTimeZone, consentGiven: consent, whatsappEnabled: true
    };
    const response = await bookEvent(eventData.id, payload);
    if (response.status === 200) {
      setSuccess(true);
    } else {
      showAlert("error", response.message || "Booking failed");
    }
  };

  const initPaidCheckout = async () => {
    const sdkLoaded = await loadRazorpayScript();
    if (!sdkLoaded) { showAlert("error", "Payment service failed to load. Try again."); return; }
    const fullMobileNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    const orderPayload = {
      eventId: eventData.id, name: name.trim(), email: email.trim(),
      mobile: fullMobileNumber.trim(), countryCode: dialCode, timeZone: userTimeZone
    };
    const orderResponse = await createEventPaymentOrder(orderPayload);
    if (orderResponse.status !== 200 && orderResponse.status !== 201) {
      showAlert("error", orderResponse.message || "Unable to start payment");
      return;
    }
    const { orderId, amount, currency } = orderResponse.data;
    openRazorpay(orderId, amount, currency, fullMobileNumber);
  };

  const openRazorpay = (orderId, amount, currency, fullMobileNumber) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount, currency,
      name: "Speak Bold with KK",
      description: eventData.title,
      order_id: orderId,
      prefill: { name: name.trim(), email: email.trim(), contact: fullMobileNumber },
      notes: { eventId: eventData.id, email: email.trim() },
      handler: (response) => { verifyAndBook(response, fullMobileNumber); },
      modal: { ondismiss: () => { showAlert("info", "Payment cancelled"); } },
      theme: { color: "#ea580c" }
    };
    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const verifyAndBook = async (paymentResponse, fullMobileNumber) => {
    try {
      setLoading(true);
      const verifyPayload = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        eventId: eventData.id, name: name.trim(), email: email.trim(),
        mobile: fullMobileNumber.trim(), countryCode: dialCode,
        timeZone: userTimeZone, consentGiven: consent, whatsappEnabled: true
      };
      const response = await verifyEventPayment(verifyPayload);
      if (response.status === 200) {
        setSuccess(true);
      } else {
        showAlert("error", response.message || "Payment verification failed");
      }
    } catch (error) {
      showAlert("error", "Payment verification failed. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  const isLoggedIn = !!user;
  const isFree = eventData.price <= 0;
  const hasDiscount = eventData.originalPrice > 0 && eventData.originalPrice > eventData.price;

  // SUCCESS UI
  if (success) {
    return (
      <>
        <Navbar />
        <div className="bk-success-wrap">
          <div className="bk-success-card">
            <div className="bk-success-icon">🎉</div>
            <h2>Booking Confirmed!</h2>
            <p>Your seat has been booked for <strong>{eventData.title}</strong>.</p>
            <div className="bk-success-details">
              <div className="bk-success-row">📅 {formatDate(eventData.eventStartTime)}</div>
              <div className="bk-success-row">⏰ {formatTime(eventData.eventStartTime)}</div>
              <div className="bk-success-row">📧 Confirmation sent to {email}</div>
            </div>
            <div className="bk-success-actions">
              <button className="bk-cta-btn" onClick={() => navigate("/events")}>View All Events</button>
              <button className="bk-outline-btn" onClick={() => navigate("/my-bookings")}>View My Bookings</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // MAIN UI
  return (
    <>
      <Navbar />

      {alert.show && (
        <div className="alert-overlay">
          <div className={`alert-modal ${alert.type}`}>
            <div className="alert-message">{alert.message}</div>
            <button className="alert-btn" onClick={() => setAlert(prev => ({ ...prev, show: false }))}>OK</button>
          </div>
        </div>
      )}

      {/* TOP BANNER */}
      {eventData.topBannerText && (
        <div className="bk-top-banner">
          {eventData.topBannerText}
        </div>
      )}

      <div className="bk-page-wrap">

        {/* BACK BUTTON */}
        <div className="bk-nav-row">
          <button className="bk-back-btn" onClick={() => navigate("/")}>← Back to Home</button>
          <button className="bk-my-bookings-btn" onClick={() => navigate("/my-bookings")}>My Bookings</button>
        </div>

        {/* HEADER AREA */}
        <div className="bk-header-area">
          <div className="bk-live-badge">🔴 LIVE {(eventData.category || "Workshop").toUpperCase()}</div>
          <h1 className="bk-main-heading">{eventData.title}</h1>
          <p className="bk-subtitle">{eventData.description || "Master public speaking and confident communication in just 3 hours. Speak with clarity, authority, and zero hesitation in meetings, presentations, and social settings."}</p>

          {/* Highlights */}
          {eventData.highlights.length > 0 && (
            <div className="bk-highlights">
              {eventData.highlights.map((h, i) => (
                <div key={i} className="bk-highlight-pill">{h}</div>
              ))}
            </div>
          )}

          <div className="bk-price-row">
            {hasDiscount && <span className="bk-price-original">₹{eventData.originalPrice}</span>}
            {isFree ? (
              <span className="bk-price-main bk-price-free">FREE</span>
            ) : (
              <>
                <span className="bk-price-main">₹{eventData.price}</span>
                {discountPct > 0 && <span className="bk-discount-badge">{discountPct}% OFF</span>}
              </>
            )}
          </div>
        </div>

        {/* CHECKOUT CARD */}
        <div className="bk-checkout-card">

          {/* Card Header */}
          <div className="bk-card-header">
            <div className="bk-card-label">SPEAK BOLD LIVE {(eventData.category || "WORKSHOP").toUpperCase()}</div>
            <div className="bk-card-title">Secure Your Seat</div>
          </div>

          {/* Social Proof */}
          {eventData.totalAttendees != null && (
            <div className="bk-social-proof">
              <div className="bk-avatars">
                {["👩", "👨", "👩🏽", "👨🏾", "👩🏻"].map((a, i) => (
                  <span key={i} className="bk-avatar">{a}</span>
                ))}
              </div>
              <span className="bk-social-text">Join <strong>{formatAttendees(eventData.totalAttendees)}</strong> professionals</span>
            </div>
          )}

          {/* Urgency Box */}
          {eventData.seatsLeft != null && (
            <div className="bk-urgency-box">
              🔥 Only <strong>{eventData.seatsLeft} seats</strong> left at this price
            </div>
          )}

          {/* Instructor Card */}
          <div className="bk-instructor-card">
            <div className="bk-instructor-avatar">
              {eventData.speakerImageUrl
                ? <img src={eventData.speakerImageUrl} alt={eventData.speakerName} className="bk-instructor-img" />
                : <span>👨‍🏫</span>}
            </div>
            <div className="bk-instructor-info">
              <div className="bk-instructor-name">{eventData.speakerName}</div>
              <div className="bk-instructor-title">{eventData.speakerBio}</div>
            </div>
          </div>

          {/* Event Details Grid */}
          <div className="bk-details-grid">
            <div className="bk-detail-pill">🎥 {eventData.eventDuration >= 60 ? `${Math.round(eventData.eventDuration / 60)} Hours Total` : `${eventData.eventDuration} Min`}</div>
            <div className="bk-detail-pill">💻 Live Class</div>
            <div className="bk-detail-pill bk-detail-pill--large">📅 {formatDate(eventData.eventStartTime)}</div>
            <div className="bk-detail-pill bk-detail-pill--large">⏰ {formatTime(eventData.eventStartTime)}</div>
          </div>

          {/* Bonus Callout */}
          {(eventData.bonusDeadline || eventData.bonusValue) && (
            <div className="bk-bonus-callout">
              Register Before <strong>{formatDate(eventData.bonusDeadline)}</strong>
              {eventData.bonusValue && (
                <> to Unlock <span className="bk-bonus-value">₹{eventData.bonusValue.toLocaleString("en-IN")} Worth of Bonuses</span></>
              )}
            </div>
          )}

          {/* Registration Form */}
          <div className="bk-divider"></div>
          <div className="bk-form-section">

            {!isLoggedIn ? (
              <>
                <div className="bk-input-group">
                  <label>First Name</label>
                  <input type="text" placeholder="Enter your first name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
                </div>
                <div className="bk-input-group">
                  <label>Email ID</label>
                  <input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
                </div>
                <div className="bk-input-group">
                  <label>WhatsApp Number</label>
                  <div className="bk-mobile-row">
                    <select className="bk-dial-select" value={dialCode} onChange={e => setDialCode(e.target.value)}>
                      {countryCodes.length > 0
                        ? countryCodes.map(c => <option key={c.id} value={c.dialCode}>{c.isoCode} {c.dialCode}</option>)
                        : <option value="+91">IN +91</option>}
                    </select>
                    <input type="tel" placeholder="WhatsApp number" value={mobile} onChange={e => { setMobile(e.target.value.replace(/[^\d]/g, "")); setMobileError(""); }} maxLength={15} autoComplete="tel" />
                  </div>
                  {mobileError && <span className="bk-field-error">{mobileError}</span>}
                </div>
              </>
            ) : (
              <div className="bk-logged-info">
                <div className="bk-logged-row"><span>👤</span><div><div className="bk-logged-label">Name</div><div className="bk-logged-val">{name}</div></div></div>
                <div className="bk-logged-row"><span>📧</span><div><div className="bk-logged-label">Email</div><div className="bk-logged-val">{email}</div></div></div>
                {mobile ? (
                  <div className="bk-logged-row"><span>📱</span><div><div className="bk-logged-label">WhatsApp</div><div className="bk-logged-val">{dialCode} {mobile}</div></div></div>
                ) : (
                  <div className="bk-input-group" style={{ marginTop: 12 }}>
                    <label>WhatsApp Number</label>
                    <div className="bk-mobile-row">
                      <select className="bk-dial-select" value={dialCode} onChange={e => setDialCode(e.target.value)}>
                        {countryCodes.length > 0
                          ? countryCodes.map(c => <option key={c.id} value={c.dialCode}>{c.isoCode} {c.dialCode}</option>)
                          : <option value="+91">IN +91</option>}
                      </select>
                      <input type="tel" placeholder="WhatsApp number" value={mobile} onChange={e => { setMobile(e.target.value.replace(/[^\d]/g, "")); setMobileError(""); }} maxLength={15} autoComplete="tel" />
                    </div>
                    {mobileError && <span className="bk-field-error">{mobileError}</span>}
                  </div>
                )}
              </div>
            )}

            {/* Payment Breakdown */}
            <div className="bk-payment-breakdown">
              <div className="bk-breakdown-row">
                <span>Workshop Fee</span>
                {hasDiscount && <span className="bk-breakdown-original">₹{eventData.originalPrice}</span>}
              </div>
              {hasDiscount && (
                <div className="bk-breakdown-row bk-breakdown-discount">
                  <span>Discount ({discountPct}% OFF)</span>
                  <span>− ₹{eventData.originalPrice - eventData.price}</span>
                </div>
              )}
              <div className="bk-breakdown-total">
                <span>Total</span>
                <span>{isFree ? "FREE" : `₹${eventData.price}`}</span>
              </div>
            </div>

            {/* Consent */}
            <label className="bk-consent">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
              <span>I agree to receive event updates and communications via WhatsApp & Email</span>
            </label>

            {/* CTA Button */}
            <button
              className={`bk-cta-btn bk-cta-full${loading || !consent ? " bk-cta-disabled" : ""}`}
              onClick={handleBooking}
              disabled={loading || !consent}
            >
              {loading ? (
                <><span className="bk-btn-spinner"></span> Processing...</>
              ) : isFree ? (
                <>✅ Confirm Booking — Free</>
              ) : (
                <>💳 Pay ₹{eventData.price} & Secure My Seat</>
              )}
            </button>

            <div className="bk-trust-row">
              {!isFree && <span>🔒 Secure Payment</span>}
              {!isFree && <span>✅ Instant Confirmation</span>}
              {!isFree && <span>💳 Razorpay</span>}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default BookingPage;
